"use client";

import { useTelegramContext } from "@/hooks/useTelegramContext";
import { ensureTelegramSdkMounted, getTelegramInitData, markTelegramReady } from "@/utils/telegramWebApp";
import { useCallback, useEffect } from "react";

type TelegramBootDebugState = {
  phase?: string;
  reason?: string;
  ready?: boolean;
  mounted?: boolean;
  hasInitData?: boolean;
  initDataLength?: number;
  loginStatus?: "idle" | "pending" | "success" | "failed";
  timestamp?: number;
};

function updateTelegramBootDebugState(patch: Partial<TelegramBootDebugState>) {
  if (typeof window === "undefined") return;

  const current = ((window as Window & { __tgDebugBoot?: TelegramBootDebugState }).__tgDebugBoot ?? {}) as TelegramBootDebugState;
  (window as Window & { __tgDebugBoot?: TelegramBootDebugState }).__tgDebugBoot = {
    ...current,
    ...patch,
    timestamp: Date.now(),
  };
}

export function useTelegramBootstrapState() {
  const isTelegram = useTelegramContext();

  useEffect(() => {
    if (!isTelegram) {
      updateTelegramBootDebugState({
        phase: "skipped",
        reason: "not-telegram-context",
        loginStatus: "idle",
      });
      return;
    }

    updateTelegramBootDebugState({
      phase: "mounting",
      reason: "tma-login-disabled",
      loginStatus: "idle",
    });

    void ensureTelegramSdkMounted().then((mounted) => {
      updateTelegramBootDebugState({
        phase: mounted ? "ready" : "mount-failed",
        mounted,
        ready: mounted,
        hasInitData: Boolean(getTelegramInitData()),
        initDataLength: getTelegramInitData().length,
        loginStatus: "idle",
      });

      if (mounted) {
        markTelegramReady();
      }
    });
  }, [isTelegram]);

  const hideLoginFailedNotice = useCallback(() => {
    // TMA API login is temporarily disabled, so there is no TG-specific failure notice to hide.
  }, []);

  return {
    showLoginFailedNotice: false,
    hideLoginFailedNotice,
  };
}
